<template>
  <button
    class="lg-icon-btn"
    :class="[`s-${size}`, { active }]"
    :type="type"
    :disabled="disabled"
    :aria-label="label"
    :aria-pressed="pressed"
    :title="label"
  >
    <slot />
  </button>
</template>

<script setup lang="ts">
withDefaults(
  defineProps<{
    /** Accessible label — required since the button has no text. */
    label: string;
    size?: 'sm' | 'md';
    type?: 'button' | 'submit' | 'reset';
    disabled?: boolean;
    active?: boolean;
    /** For toggle buttons: reflects pressed state to AT. */
    pressed?: boolean;
  }>(),
  { size: 'md', type: 'button', disabled: false, active: false },
);
</script>

<style scoped>
.lg-icon-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--lg-radius-md);
  border: 1px solid transparent;
  background: transparent;
  color: var(--lg-text-dim);
  cursor: pointer;
  transition:
    color var(--lg-dur-fast) var(--lg-ease),
    background var(--lg-dur-fast) var(--lg-ease),
    border-color var(--lg-dur-fast) var(--lg-ease);
}
.s-sm {
  width: var(--lg-control-sm);
  height: var(--lg-control-sm);
}
.s-md {
  width: var(--lg-control-md);
  height: var(--lg-control-md);
}
.lg-icon-btn:hover:not(:disabled) {
  color: var(--lg-text);
  background: var(--lg-bg-hover);
}
.lg-icon-btn.active {
  color: var(--lg-accent);
  background: var(--lg-bg-active);
}
.lg-icon-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.lg-icon-btn:focus-visible {
  outline: none;
  box-shadow: var(--lg-focus);
}

/* Expand the hit area to the minimum comfortable touch target on mobile
   without growing the visible control — a centered invisible pseudo-element
   that overflows the button's own (28/34px) box up to --lg-touch (44px). */
@media (max-width: 768px) {
  .lg-icon-btn::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: var(--lg-touch);
    height: var(--lg-touch);
    transform: translate(-50%, -50%);
  }
}
</style>
